import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import UserDetailModal from "@/components/UserDetailModal";
import { Users, MagnifyingGlass, UserPlus } from "@phosphor-icons/react";
import { CuteLoader } from "@/components/CuteLoader";

export default function AdminTeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (userId) => {
    setSelectedUserId(userId);
    setShowUserDetail(true);
  };

  const handleUserSaved = () => {
    loadUsers();
  };

  const filteredUsers = users.filter(u => 
    u.is_active && 
    u.role !== "administrator" &&
    (u.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     u.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return <CuteLoader />;
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="border-b border-border bg-card px-4 py-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Team Management</h1>
        <p className="text-sm text-muted-foreground mt-1">View and manage employee accounts</p>
      </div>

      <div className="p-4 space-y-4">
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input 
            type="search"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Active Team ({filteredUsers.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No team members found
              </div>
            ) : (
              <div className="divide-y">
                {filteredUsers.map((u) => (
                  <button
                    key={u._id}
                    onClick={() => handleUserClick(u._id)}
                    className="w-full text-left p-4 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground">{u.display_name || u.name}</h3>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs bg-muted px-2 py-1 rounded">
                            {u.employment_role || u.role}
                          </span>
                          {u.hourly_rate && (
                            <span className="text-xs text-muted-foreground">
                              ${u.hourly_rate}/hr
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-muted-foreground">›</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <UserDetailModal 
        userId={selectedUserId}
        open={showUserDetail}
        onClose={() => setShowUserDetail(false)}
        onSave={handleUserSaved}
      />
    </div>
  );
}
